import { computeWalletApplication } from './computeWalletApplication';

export interface WalletPlanChargeInput {
  priceCents: number;
  availableCents: number;
  discountPercent?: number;
}

export interface WalletPlanChargeResult {
  appliedCents: number;
  remainingCents: number;
  coversFully: boolean;
  discountCents: number;
  catalogCoveredCents: number;
  discountPercent: number;
}

/**
 * How much a wallet payment covers after the plan-tier discount.
 * The discount applies only to the catalog share funded by wallet.
 */
export function computeWalletPlanCharge({
  priceCents,
  availableCents,
  discountPercent = 0,
}: WalletPlanChargeInput): WalletPlanChargeResult {
  const rate = Number(discountPercent) || 0;
  if (rate <= 0 || priceCents <= 0) {
    const base = computeWalletApplication({ availableCents, priceCents });
    return {
      ...base,
      discountCents: 0,
      catalogCoveredCents: base.appliedCents,
      discountPercent: 0,
    };
  }

  const payableFull = Math.round((priceCents * (100 - rate)) / 100);
  const available = Math.max(0, availableCents);

  if (available >= payableFull) {
    return {
      appliedCents: payableFull,
      remainingCents: 0,
      coversFully: true,
      discountCents: priceCents - payableFull,
      catalogCoveredCents: priceCents,
      discountPercent: rate,
    };
  }

  if (available <= 0) {
    return {
      appliedCents: 0,
      remainingCents: priceCents,
      coversFully: false,
      discountCents: 0,
      catalogCoveredCents: 0,
      discountPercent: rate,
    };
  }

  const catalogCoveredCents = Math.min(
    priceCents - 1,
    Math.round((available * 100) / (100 - rate)),
  );
  const discountCents = catalogCoveredCents - available;
  const remainingCents = priceCents - catalogCoveredCents;

  return {
    appliedCents: available,
    remainingCents,
    coversFully: false,
    discountCents,
    catalogCoveredCents,
    discountPercent: rate,
  };
}
